import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";

import {
	checkLineIntersectsROIPolygon,
	checkPointInsideROIPolygon,
	getCrossingLineDirectionalArrow,
	getFormattedCrossingLines,
	getFormattedDirectionalArrowString,
	getFormattedLineString,
	getFormattedROIPolygonString,
	getLinesDetailsFromString,
	getPreviewCrossingLinesDetails,
	getPreviewDirectionalArrowDetails,
	getPreviewROIPolygonDetails,
	getROIPolygonDetailsFromString
} from "./utils";
import {
	CAMERA_SETUP_STEPS,
	CANVAS_POLYGON_COORDINATE_RADIUS,
	CANVAS_STROKE_COLOR,
	DEFAULT_NUMBER_OF_LINES
} from "./constants";
import {
	IArrow,
	ICameraCanvasUpdatedDetails,
	ICameraDetails,
	ICanvasComponentRef,
	ICanvasHistoryDetails,
	ICoordinate,
	ICrossingLine,
	IDimensions,
	LineDetailsType
} from "./interfaces";
import canvasServices from "./services";
import StatusDialog from "../StatusDialog";

interface ICanvasComponentProps {
	image: string;
	// width: number;
	// height: number;
	numberOfLines: number;
	cameraDetails: ICameraDetails | null;
	rtspUrlInput: string;
	onSaveDetails: (updateCameraDetails: ICameraCanvasUpdatedDetails) => void;
	setCurrentCanvasStep: React.Dispatch<React.SetStateAction<number>>;
	setCrossingLinesGeneration: React.Dispatch<React.SetStateAction<"auto" | "manual">>;
	setShowCanvasPreview: React.Dispatch<React.SetStateAction<boolean>>;
	setShowPreviewLoader: React.Dispatch<React.SetStateAction<boolean>>;
	setShowSubmitButton: React.Dispatch<React.SetStateAction<boolean>>;
}

const CanvasComponent = forwardRef<ICanvasComponentRef, ICanvasComponentProps>((props, ref) => {
	const {
		image,
		// width,
		// height,
		numberOfLines,
		cameraDetails,
		rtspUrlInput,
		onSaveDetails,
		setCurrentCanvasStep,
		setCrossingLinesGeneration,
		setShowCanvasPreview,
		setShowPreviewLoader,
		setShowSubmitButton
	} = props;

	const [canvasDimensions, setCanvasDimensions] = useState<IDimensions>({ height: 0, width: 0 });
	const [canvasChanged, setCanvasChanged] = useState<boolean>(false);
	const [errorDialogMessage, setErrorDialogMessage] = useState<string>("");

	const canvasRef = useRef<HTMLCanvasElement>(null);

	/**
	 * DATA TO RENDER IN CANVAS
	 */
	const ROIPolygonCoordinates = useRef<ICoordinate[]>([]); // ROI POLYGON
	const crossingLinesCoordinates = useRef<ICrossingLine[]>([]); // CROSSING LINES
	const directionalArrowCoordinates = useRef<IArrow>({
		start: null,
		end: null
	}); // DIRECTIONAL ARROW

	/**
	 * FLAGS TO CHECK IF STEP DRAWING IS IN PROGRESS
	 */
	const movingROICoordinateIndex = useRef<number>(-1); // MOVING A CO-ORDINATE OF ROI POLYGON (INDEX OF THE MOVING CO-ORDINATE)
	const drawingDiresctionalArrow = useRef(false); // DRAWING DIRECTIONAL ARROW
	const drawingCrossingLine = useRef(false); // DRAWING CROSSING LINES

	/**
	 * MAINTAIN HISTORY OF CANVAS
	 */
	const canvasDrawingHistory = useRef<ICanvasHistoryDetails[]>([]);

	/**
	 * FLAGS TO CHECK WHETHER THE STEP IS COMPLETED OR NOT
	 */
	const ROIPolygonDrawingComplete = useRef(false); // ROI POLYGON
	const directionalArrowDrawingComplete = useRef(false); // DIRECTIONAL ARROW
	const crossingLinesDrawingComplete = useRef(false); // CROSSING LINES

	function handleOpenErrorDialog(message: string) {
		setErrorDialogMessage(message);
	}

	function handleCloseErrorDialog() {
		setErrorDialogMessage("");
	}

	function renderPreview(
		ROIToRender: string,
		directionArrowToRender: string,
		crossingLinesToRender: LineDetailsType,
		finalLines: LineDetailsType
	) {
		if (!canvasRef.current) return;

		const imageDimensions = { height: 100, width: 100 };

		const previewROICoordinates = getPreviewROIPolygonDetails(ROIToRender, imageDimensions, canvasDimensions);
		const previewDirectionalArrowCoordinates = getPreviewDirectionalArrowDetails(
			directionArrowToRender,
			imageDimensions,
			canvasDimensions
		);
		const previewCrossingLinesCoordinates = getPreviewCrossingLinesDetails(
			crossingLinesToRender,
			finalLines,
			imageDimensions,
			canvasDimensions
		);

		if (previewROICoordinates.length <= 0) return;

		if (!previewDirectionalArrowCoordinates.start || !previewDirectionalArrowCoordinates.end) return;

		if (previewCrossingLinesCoordinates.length <= 0) return;

		// eslint-disable-next-line no-self-assign
		canvasRef.current.width = canvasRef.current.width;

		ROIPolygonCoordinates.current = previewROICoordinates;
		directionalArrowCoordinates.current = previewDirectionalArrowCoordinates;
		crossingLinesCoordinates.current = previewCrossingLinesCoordinates;

		redrawCanvas();
	}

	function handlePreviewCameraCanvas(changeStep = false) {
		if (ROIPolygonCoordinates.current.length < 3) {
			handleOpenErrorDialog("Please complete the ROI polygon");
			return;
		}

		if (!directionalArrowCoordinates.current.start || !directionalArrowCoordinates.current.end) {
			if (crossingLinesCoordinates.current.length <= 0) {
				handleOpenErrorDialog("Please draw crossing lines");
				return;
			}

			handleOpenErrorDialog("Please draw directional arrow");
			return;
		}

		if (canvasChanged) {
			const lineIntersectsPolygon = checkLineIntersectsROIPolygon(
				directionalArrowCoordinates.current,
				ROIPolygonCoordinates.current
			);

			if (lineIntersectsPolygon) {
				handleOpenErrorDialog("The direction arrow should be inside ROI");
				return;
			}

			const lineInsidePolygon = checkPointInsideROIPolygon(
				directionalArrowCoordinates.current.start,
				ROIPolygonCoordinates.current
			);

			if (!lineInsidePolygon) {
				handleOpenErrorDialog("The direction arrow should be inside ROI");
				return;
			}
		}

		setShowPreviewLoader(true);

		const imageDimensions = {
			height: 100,
			width: 100
		};

		const formattedROICoordinates = getFormattedROIPolygonString(
			ROIPolygonCoordinates.current,
			imageDimensions,
			canvasDimensions,
			true
		);
		const formattedDirectionArrowCoordinates = getFormattedDirectionalArrowString(
			directionalArrowCoordinates.current,
			imageDimensions,
			canvasDimensions
		);

		const formattedCrossingLinesResult = getFormattedCrossingLines(
			crossingLinesCoordinates.current,
			ROIPolygonCoordinates.current,
			canvasChanged,
			imageDimensions,
			canvasDimensions
		);

		if (!formattedDirectionArrowCoordinates && !formattedCrossingLinesResult.success) {
			handleOpenErrorDialog(formattedCrossingLinesResult.message);
			return;
		}

		const formattedCrossingLinesCoordinates = formattedDirectionArrowCoordinates
			? []
			: formattedCrossingLinesResult.result;

		if (!formattedROICoordinates) return;

		if (formattedDirectionArrowCoordinates) {
			const getCrossingLinesData = {
				roi: formattedROICoordinates,
				directional_line: formattedDirectionArrowCoordinates,
				height: 100,
				width: 100,
				num_lines: numberOfLines ? Number(numberOfLines) : DEFAULT_NUMBER_OF_LINES
			};

			canvasServices
				.getCrossingLines(getCrossingLinesData)
				.then((response) => {
					if (response && response.crossing_lines && response.directional_line && response.roi) {
						if (!changeStep) {
							ROIPolygonDrawingComplete.current = true;
							directionalArrowDrawingComplete.current = true;
							crossingLinesDrawingComplete.current = true;
						} else {
							ROIPolygonDrawingComplete.current = false;
							directionalArrowDrawingComplete.current = false;
							crossingLinesDrawingComplete.current = false;
						}

						setShowPreviewLoader(false);

						renderPreview(response.roi, response.directional_line, response.crossing_lines, response.final_lines);

						if (changeStep) setCurrentCanvasStep(3);

						setShowSubmitButton(true);
					}
				})
				.catch((err) => {
					console.error(err);
				});
		} else if (formattedCrossingLinesCoordinates.length > 0) {
			if (!changeStep) {
				ROIPolygonDrawingComplete.current = true;
				directionalArrowDrawingComplete.current = true;
				crossingLinesDrawingComplete.current = true;
			} else {
				ROIPolygonDrawingComplete.current = false;
				directionalArrowDrawingComplete.current = false;
				crossingLinesDrawingComplete.current = false;
			}

			setShowPreviewLoader(false);

			if (changeStep) setCurrentCanvasStep(3);

			setShowSubmitButton(true);
		}
	}

	function handleSubmitCameraCanvas() {
		if (ROIPolygonCoordinates.current.length <= 0) {
			handleOpenErrorDialog("Please complete the ROI polygon");
			return;
		}

		if (!directionalArrowCoordinates.current.start || !directionalArrowCoordinates.current.end) {
			handleOpenErrorDialog("Please draw directional arrow");
			return;
		}

		if (crossingLinesCoordinates.current.length <= 0) {
			handleOpenErrorDialog("Please draw crossing lines");
			return;
		}

		const imageDimensions = { height: 100, width: 100 };

		const formattedROIString = getFormattedROIPolygonString(
			ROIPolygonCoordinates.current,
			imageDimensions,
			canvasDimensions
		);

		const formattedLineString = getFormattedLineString(
			directionalArrowCoordinates.current,
			crossingLinesCoordinates.current,
			imageDimensions,
			canvasDimensions
		);

		if (!formattedROIString || !formattedLineString) {
			return;
		}

		onSaveDetails({
			roi: formattedROIString,
			line: formattedLineString,
			height: 100,
			width: 100,
			numberOfLines: numberOfLines
		});
	}

	function handleChangeCanvasStep(stepId: number) {
		setCurrentCanvasStep((currentCanvasStep) => {
			switch (currentCanvasStep) {
				case 1:
					if (ROIPolygonCoordinates.current.length < 3) {
						handleOpenErrorDialog("Please complete the ROI polygon");
						return currentCanvasStep;
					}

					return stepId;

				case 2:
					setCrossingLinesGeneration("auto");

					if (stepId === 3) {
						handlePreviewCameraCanvas(true);
						return currentCanvasStep;
					}

					return stepId;

				case 3:
					if (stepId === 2) {
						crossingLinesCoordinates.current = [];
						redrawCanvas();
					}
					return stepId;

				default:
					return stepId;
			}
		});
	}

	function drawPolygonCoordinates() {
		if (!canvasRef.current) return;

		const canvasContext = canvasRef.current.getContext("2d");
		if (!canvasContext) return;

		canvasContext.strokeStyle = CANVAS_STROKE_COLOR;
		canvasContext.lineWidth = 2;
		canvasContext.fillStyle = CANVAS_STROKE_COLOR;

		for (const coordinateItem of ROIPolygonCoordinates.current) {
			canvasContext.beginPath();
			canvasContext.arc(coordinateItem.x, coordinateItem.y, CANVAS_POLYGON_COORDINATE_RADIUS, 0, 2 * Math.PI, false);
			canvasContext.fill();
			canvasContext.stroke();
		}
	}

	function redrawPolygon() {
		if (ROIPolygonCoordinates.current.length <= 0) return;

		if (!canvasRef.current) return;

		drawPolygonCoordinates();

		const canvasContext = canvasRef.current.getContext("2d");
		if (!canvasContext) return;

		canvasContext.strokeStyle = CANVAS_STROKE_COLOR;
		canvasContext.lineWidth = 2;

		canvasContext.moveTo(ROIPolygonCoordinates.current[0].x, ROIPolygonCoordinates.current[0].y);

		for (const coordinateItem of ROIPolygonCoordinates.current) {
			canvasContext.lineTo(coordinateItem.x, coordinateItem.y);
		}

		canvasContext.closePath();
		canvasContext.stroke();
	}

	function drawArrowHead(
		startCoordinate: ICoordinate,
		endCoordinate: ICoordinate,
		canvasContext: CanvasRenderingContext2D,
		arrowHeadLength: number
	) {
		const distanceX = endCoordinate.x - startCoordinate.x;
		const distanceY = endCoordinate.y - startCoordinate.y;

		const angle = Math.atan2(distanceY, distanceX);

		canvasContext.lineTo(
			endCoordinate.x - arrowHeadLength * Math.cos(angle - Math.PI / 6),
			endCoordinate.y - arrowHeadLength * Math.sin(angle - Math.PI / 6)
		);

		canvasContext.moveTo(endCoordinate.x, endCoordinate.y);

		canvasContext.lineTo(
			endCoordinate.x - arrowHeadLength * Math.cos(angle + Math.PI / 6),
			endCoordinate.y - arrowHeadLength * Math.sin(angle + Math.PI / 6)
		);
	}

	function redrawArrow() {
		if (!canvasRef.current) return;

		if (!directionalArrowCoordinates.current.start || !directionalArrowCoordinates.current.end) return;

		const canvasContext = canvasRef.current.getContext("2d");
		if (!canvasContext) return;

		canvasContext.strokeStyle = CANVAS_STROKE_COLOR;
		canvasContext.lineWidth = 2;

		canvasContext.moveTo(directionalArrowCoordinates.current.start.x, directionalArrowCoordinates.current.start.y);

		canvasContext.lineTo(directionalArrowCoordinates.current.end.x, directionalArrowCoordinates.current.end.y);
		canvasContext.moveTo(directionalArrowCoordinates.current.end.x, directionalArrowCoordinates.current.end.y);

		drawArrowHead(
			directionalArrowCoordinates.current.start,
			directionalArrowCoordinates.current.end,
			canvasContext,
			10
		);

		canvasContext.stroke();
	}

	function redrawCrossingLines() {
		if (!canvasRef.current) return;

		if (crossingLinesCoordinates.current.length <= 0) return;

		const canvasContext = canvasRef.current.getContext("2d");
		if (!canvasContext) return;

		canvasContext.strokeStyle = CANVAS_STROKE_COLOR;
		canvasContext.lineWidth = 2;

		for (const crossingLineItem of crossingLinesCoordinates.current) {
			if (!crossingLineItem.start || !crossingLineItem.end) continue;

			canvasContext.moveTo(crossingLineItem.start.x, crossingLineItem.start.y);
			canvasContext.lineTo(crossingLineItem.end.x, crossingLineItem.end.y);

			// if (crossingLineItem.directionLineCoordinates.start && crossingLineItem.directionLineCoordinates.end) {
			// 	drawPerpendicularLine(crossingLineItem.directionLineCoordinates, canvasContext);
			// }
		}

		canvasContext.stroke();
	}

	function redrawCanvas() {
		if (canvasRef.current) {
			// CLEAR THE CANVAS
			// eslint-disable-next-line no-self-assign
			canvasRef.current.width = canvasRef.current.width;
		}

		redrawPolygon();
		redrawArrow();
		redrawCrossingLines();
	}

	function findClickedCoordinate(newCoordinate: ICoordinate): { type: "roi_coordinate"; index: number } | null {
		for (const [index, coordinateItem] of Object.entries(ROIPolygonCoordinates.current)) {
			const differenceX = (newCoordinate.x - coordinateItem.x) * (newCoordinate.x - coordinateItem.x);
			const differenceY = (newCoordinate.y - coordinateItem.y) * (newCoordinate.y - coordinateItem.y);

			const distance = Math.sqrt(differenceX + differenceY);

			if (distance <= CANVAS_POLYGON_COORDINATE_RADIUS) {
				return {
					type: "roi_coordinate",
					index: Number(index)
				};
			}
		}

		return null;
	}

	function handleMoveROICoordinate(updatedCoordinate: ICoordinate) {
		ROIPolygonCoordinates.current[movingROICoordinateIndex.current] = updatedCoordinate;
		redrawCanvas();
	}

	function handleUndoCanvasDrawing() {
		if (canvasDrawingHistory.current.length <= 0) return;

		const lastMove = canvasDrawingHistory.current[canvasDrawingHistory.current.length - 1];

		switch (lastMove.type) {
			case "add_roi_coordinate":
				ROIPolygonCoordinates.current.pop();
				setCurrentCanvasStep(1);
				ROIPolygonDrawingComplete.current = false;
				directionalArrowDrawingComplete.current = false;
				crossingLinesDrawingComplete.current = false;
				break;

			case "move_roi_coordinate":
				if (lastMove.index && lastMove.coordinate) {
					ROIPolygonCoordinates.current[lastMove.index] = lastMove.coordinate;
				}
				setCurrentCanvasStep(1);
				ROIPolygonDrawingComplete.current = false;
				directionalArrowDrawingComplete.current = false;
				crossingLinesDrawingComplete.current = false;
				break;

			case "draw_directional_arrow":
				if (lastMove.directionalLine) {
					directionalArrowCoordinates.current = lastMove.directionalLine;
				} else {
					directionalArrowCoordinates.current = {
						start: null,
						end: null
					};
				}
				setCurrentCanvasStep(2);
				setCrossingLinesGeneration("auto");
				ROIPolygonDrawingComplete.current = false;
				directionalArrowDrawingComplete.current = false;
				crossingLinesDrawingComplete.current = false;
				crossingLinesCoordinates.current = [];
				break;

			case "draw_crossing_line":
				crossingLinesCoordinates.current.pop();

				setCurrentCanvasStep(3);
				setCrossingLinesGeneration("manual");
				break;

			case "change_crossing_lines_generation":
				if (lastMove.crossingLines) {
					crossingLinesCoordinates.current = lastMove.crossingLines;

					setCurrentCanvasStep(3);
					setCrossingLinesGeneration("manual");
				} else if (lastMove.directionalLine) {
					directionalArrowCoordinates.current = lastMove.directionalLine;

					setCurrentCanvasStep(3);
					setCrossingLinesGeneration("auto");
					handlePreviewCameraCanvas();
				}
				break;

			default:
				break;
		}

		if (canvasRef.current) {
			// eslint-disable-next-line no-self-assign
			canvasRef.current.width = canvasRef.current.width;
			redrawCanvas();
		}

		canvasDrawingHistory.current.pop();
	}

	function handleResetCanvas() {
		if (!canvasRef.current) return;

		// CLEAR THE CANVAS
		// eslint-disable-next-line no-self-assign
		canvasRef.current.width = canvasRef.current.width;

		ROIPolygonCoordinates.current = [];
		ROIPolygonDrawingComplete.current = false;

		directionalArrowCoordinates.current = {
			start: null,
			end: null
		};
		directionalArrowDrawingComplete.current = false;

		crossingLinesCoordinates.current = [];
		crossingLinesDrawingComplete.current = false;

		setShowCanvasPreview(false);
		setShowSubmitButton(false);
		setCurrentCanvasStep(1);
	}

	function handleEditCanvasPreview() {
		if (!canvasRef.current) return;

		// eslint-disable-next-line no-self-assign
		canvasRef.current.width = canvasRef.current.width;

		ROIPolygonDrawingComplete.current = false;
		directionalArrowDrawingComplete.current = false;
		crossingLinesDrawingComplete.current = false;

		if (ROIPolygonCoordinates.current.length <= 0) {
			setCurrentCanvasStep(1);
		}

		setShowCanvasPreview(false);
		setShowSubmitButton(false);
		redrawCanvas();
	}

	function canvasArrowMouseDownListener(event: any) {
		setCurrentCanvasStep((currentCanvasStep) => {
			switch (currentCanvasStep) {
				// ROI COORDINATE
				case 1:
					const coordinate = {
						x: event.offsetX,
						y: event.offsetY
					};

					const result = findClickedCoordinate(coordinate);

					if (result && result.type === "roi_coordinate") {
						handleMoveROICoordinate(coordinate);
						movingROICoordinateIndex.current = result.index;

						canvasDrawingHistory.current.push({
							type: "move_roi_coordinate",
							coordinate: ROIPolygonCoordinates.current[result.index],
							index: result.index
						});
					}
					break;

				// DIRECTION ARROW
				case 2:
					if (!directionalArrowDrawingComplete.current) {
						if (directionalArrowCoordinates.current.start && directionalArrowCoordinates.current.end) {
							canvasDrawingHistory.current.push({
								type: "draw_directional_arrow",
								directionalLine: directionalArrowCoordinates.current
							});
						} else {
							canvasDrawingHistory.current.push({
								type: "draw_directional_arrow"
							});
						}

						drawingDiresctionalArrow.current = true;
						directionalArrowCoordinates.current = {
							start: {
								x: event.offsetX,
								y: event.offsetY
							},
							end: null
						};

						redrawCanvas();
					}
					break;

				// CROSSING LINE
				case 3:
					setCrossingLinesGeneration((crossingLinesGeneration) => {
						if (crossingLinesGeneration === "manual" && !crossingLinesDrawingComplete.current) {
							drawingCrossingLine.current = true;
							crossingLinesCoordinates.current.push({
								start: {
									x: event.offsetX,
									y: event.offsetY
								},
								end: null,
								directionLineCoordinates: {
									start: null,
									end: null
								},
								isFinalLine: true
							});

							redrawCanvas();
						}

						return crossingLinesGeneration;
					});
					break;

				default:
					break;
			}

			return currentCanvasStep;
		});

		setCanvasChanged(true);
	}

	function canvasArrowMouseMoveListener(event: any) {
		setCurrentCanvasStep((currentCanvasStep) => {
			switch (currentCanvasStep) {
				// ROI COORDINATE
				case 1:
					if (movingROICoordinateIndex.current >= 0) {
						const coordinate = {
							x: event.offsetX,
							y: event.offsetY
						};

						handleMoveROICoordinate(coordinate);
					}
					break;

				// DIRECTION ARROW
				case 2:
					if (drawingDiresctionalArrow.current && !directionalArrowDrawingComplete.current) {
						directionalArrowCoordinates.current.end = {
							x: event.offsetX,
							y: event.offsetY
						};

						redrawCanvas();

						setCanvasChanged(true);
					}
					break;

				// CROSSING LINE
				case 3:
					setCrossingLinesGeneration((crossingLinesGeneration) => {
						if (
							crossingLinesGeneration === "manual" &&
							drawingCrossingLine.current &&
							!crossingLinesDrawingComplete.current
						) {
							const lastCrossingLine = crossingLinesCoordinates.current[crossingLinesCoordinates.current.length - 1];
							const coordinate = {
								x: event.offsetX,
								y: event.offsetY
							};

							crossingLinesCoordinates.current = crossingLinesCoordinates.current.slice(
								0,
								crossingLinesCoordinates.current.length - 1
							);

							if (lastCrossingLine.start) {
								crossingLinesCoordinates.current.push({
									...lastCrossingLine,
									end: coordinate,
									directionLineCoordinates: getCrossingLineDirectionalArrow(lastCrossingLine.start, coordinate)
								});
							}

							redrawCanvas();

							setCanvasChanged(true);
						}

						return crossingLinesGeneration;
					});
					break;

				default:
					break;
			}

			return currentCanvasStep;
		});
	}

	function canvasArrowMouseUpListener(event: any, isMouseOut = false) {
		setCurrentCanvasStep((currentCanvasStep) => {
			switch (currentCanvasStep) {
				// ROI COORDINATE
				case 1:
					if (movingROICoordinateIndex.current >= 0) {
						const coordinate = {
							x: event.offsetX,
							y: event.offsetY
						};

						const canvasElementDimensions = {
							width: event.target?.width ?? 0,
							height: event.target?.height ?? 0
						};

						if (coordinate.x < 0) coordinate.x = 0;
						if (coordinate.y < 0) coordinate.y = 0;

						if (coordinate.x > canvasElementDimensions.width) coordinate.x = canvasElementDimensions.width;
						if (coordinate.y > canvasElementDimensions.height) coordinate.y = canvasElementDimensions.height;

						handleMoveROICoordinate(coordinate);

						if (isMouseOut) movingROICoordinateIndex.current = -1;
					}
					break;

				// DIRECTION ARROW
				case 2:
					if (drawingDiresctionalArrow.current && !directionalArrowDrawingComplete.current) {
						drawingDiresctionalArrow.current = false;
						directionalArrowCoordinates.current.end = {
							x: event.offsetX,
							y: event.offsetY
						};

						redrawCanvas();

						setCanvasChanged(true);
					}
					break;

				// CROSSING LINE
				case 3:
					setCrossingLinesGeneration((crossingLinesGeneration) => {
						if (
							crossingLinesGeneration === "manual" &&
							drawingCrossingLine.current &&
							!crossingLinesDrawingComplete.current
						) {
							drawingCrossingLine.current = false;

							const lastCrossingLine = crossingLinesCoordinates.current[crossingLinesCoordinates.current.length - 1];
							const coordinate = {
								x: event.offsetX,
								y: event.offsetY
							};

							crossingLinesCoordinates.current = crossingLinesCoordinates.current.slice(
								0,
								crossingLinesCoordinates.current.length - 1
							);

							if (lastCrossingLine.start) {
								crossingLinesCoordinates.current.push({
									...lastCrossingLine,
									end: coordinate,
									directionLineCoordinates: getCrossingLineDirectionalArrow(lastCrossingLine.start, coordinate)
								});
							}

							redrawCanvas();

							canvasDrawingHistory.current.push({
								type: "draw_crossing_line"
							});

							setCanvasChanged(true);
						}

						return crossingLinesGeneration;
					});
					break;

				default:
					break;
			}

			return currentCanvasStep;
		});
	}

	function canvasArrowMouseOutListener(event: any) {
		canvasArrowMouseUpListener(event, true);
	}

	function canvasPolygonClickListener(event: any) {
		setCurrentCanvasStep((currentCanvasStep) => {
			if (currentCanvasStep === 1 && !ROIPolygonDrawingComplete.current && movingROICoordinateIndex.current < 0) {
				const coordinate = {
					x: event.offsetX,
					y: event.offsetY
				};

				ROIPolygonCoordinates.current.push(coordinate);
				canvasDrawingHistory.current.push({
					type: "add_roi_coordinate",
					coordinate: coordinate
				});

				redrawCanvas();
			}

			movingROICoordinateIndex.current = -1;

			return currentCanvasStep;
		});
	}

	useEffect(() => {
		if (image && canvasRef.current && canvasRef.current.offsetWidth) {
			setCanvasDimensions((currentDimensions) => ({
				...currentDimensions,
				width: canvasRef.current?.offsetWidth ?? 0
			}));
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [canvasRef.current, image]);

	useEffect(() => {
		if (canvasDimensions.width) {
			const canvasHeight = (canvasDimensions.width * 9) / 16;

			setCanvasDimensions((currentCanvasDimensions) => ({
				...currentCanvasDimensions,
				height: canvasHeight
			}));
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [image, canvasDimensions.width]);

	useEffect(() => {
		if (canvasRef.current && image) {
			canvasRef.current.removeEventListener("mousedown", canvasArrowMouseDownListener);
			canvasRef.current.removeEventListener("mousemove", canvasArrowMouseMoveListener);
			canvasRef.current.removeEventListener("mouseup", canvasArrowMouseUpListener);
			canvasRef.current.removeEventListener("mouseout", canvasArrowMouseOutListener);

			canvasRef.current.removeEventListener("click", canvasPolygonClickListener);

			canvasRef.current.addEventListener("click", canvasPolygonClickListener);
			canvasRef.current.addEventListener("mousedown", canvasArrowMouseDownListener);
			canvasRef.current.addEventListener("mousemove", canvasArrowMouseMoveListener);
			canvasRef.current.addEventListener("mouseup", canvasArrowMouseUpListener);
			canvasRef.current.addEventListener("mouseout", canvasArrowMouseOutListener);
		}

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [canvasRef, image]);

	useEffect(() => {
		// const imageHeight = height;
		// const imageWidth = width;

		if (
			cameraDetails &&
			cameraDetails.roi &&
			cameraDetails.line &&
			// imageHeight &&
			// imageWidth &&
			canvasDimensions.height &&
			canvasDimensions.width &&
			cameraDetails.rtsp_uri === rtspUrlInput
		) {
			const imageDimensions = {
				height: 100,
				width: 100
			};

			const ROIArray = getROIPolygonDetailsFromString(cameraDetails.roi, imageDimensions, canvasDimensions);
			const linesData = getLinesDetailsFromString(cameraDetails.line, imageDimensions, canvasDimensions);

			const linesArray = linesData.crossingLinesArray;
			const directionLine = linesData.directionalLine;

			if (canvasRef.current) {
				// eslint-disable-next-line no-self-assign
				canvasRef.current.width = canvasRef.current.width;

				ROIPolygonCoordinates.current = ROIArray;
				directionalArrowCoordinates.current = directionLine;
				crossingLinesCoordinates.current = linesArray;

				ROIPolygonDrawingComplete.current = true;
				directionalArrowDrawingComplete.current = true;
				crossingLinesDrawingComplete.current = true;

				setShowCanvasPreview(true);
				setShowSubmitButton(true);

				setCurrentCanvasStep(CAMERA_SETUP_STEPS[CAMERA_SETUP_STEPS.length - 1].id + 1);
				redrawCanvas();
			}
		}

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [cameraDetails, canvasDimensions.height]);

	useImperativeHandle(ref, () => ({
		changeCanvasStep: handleChangeCanvasStep,
		undoCanvasDrawing: handleUndoCanvasDrawing,
		resetCanvas: handleResetCanvas,
		editCanvas: handleEditCanvasPreview,
		previewCameraCanvas: handlePreviewCameraCanvas,
		submitCameraCanvas: handleSubmitCameraCanvas
	}));

	return (
		<>
			<canvas
				ref={canvasRef}
				width={canvasDimensions.width}
				height={canvasDimensions.height}
				style={{
					backgroundImage: `url(data:image/png;base64,${image})`,
					backgroundSize: "contain",
					backgroundPosition: "center",
					width: "100%",
					backgroundRepeat: "no-repeat"
				}}
				hidden={!image}
			/>

			<StatusDialog
				open={!!errorDialogMessage}
				message={errorDialogMessage}
				type="error"
				onClose={handleCloseErrorDialog}
			/>
		</>
	);
});

export default CanvasComponent;
